import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { useCreateResource } from '@app/src/api/updateHooks'
import Select from '@app/src/components/Form/Select/ControlledSelect'
import { SelectButton } from '@app/src/components/Form/Select/SelectButton'
import LoadingButton from '@app/src/components/LoadingButton'
import ErrorDisplay from '@app/src/components/Ui/ErrorDisplay'
import { useAccessibleOrganizations } from '@app/src/context/AccessibleOrganizationsContext'
import { useAccount } from '@app/src/context/AccountContext'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import RequestError from '@app/src/errors/RequestError'
import useErrorNotification from '@app/src/hooks/errorNotification'
import { useUnsetReferral } from '@app/src/hooks/referral'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import OnboardingForm from '@app/src/pages/SolutionSelector/OnboardingForm'
import { SortOrder } from '@app/src/types/filter'
import { Claim, CompanyRanges, NaceCode } from '@app/src/types/organizations'
import { AmplitudeTrackingEvents } from '@app/src/wf-constants'
import { ArrowBack, Check } from '@mui/icons-material'
import { Button, Checkbox, FormControl, FormControlLabel, FormLabel, Stack, Typography } from '@mui/material'
import * as Sentry from '@sentry/react'
import React, { useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useQueryClient } from 'react-query'
import { ClaimFormData, ClaimFormFields } from './ClaimDialog'

interface orgInfoStepProps {
  closeDialog: () => void
  onNextStep: () => void
  onPreviousStep: () => void
}

type NaceCodeLevelOption = {
  industryId: number
  industryName: string
  subIndustryId: number
}

enum ClaimCreationResult {
  RequiresReview = 'RequiresReview',
  AutomaticallyApproved = 'AutomaticallyApproved',
}

const OrgInfoStep: React.FC<orgInfoStepProps> = ({ closeDialog, onPreviousStep, onNextStep }) => {
  const { formatMessage } = useIntl()
  const { control, handleSubmit, errors, setError, clearErrors } = useFormContext()
  const { trackEvent } = useAmplitude()
  const { accessibleOrganizations } = useAccessibleOrganizations()
  const [consent, setConsent] = useState(false)
  const { mutateAsync, isLoading } = useCreateResource<ClaimCreationResult, Omit<Claim, 'id'>>()
  const { account } = useAccount()
  const { showErrorNotification } = useErrorNotification()
  const unsetReferral = useUnsetReferral()
  const queryClient = useQueryClient()
  const { showSnackbar } = useSnackbar()

  const referralCode = account?.referralCode

  useEffect(() => {
    trackEvent({
      name: AmplitudeTrackingEvents.Onboarding.AddOrganization.OpenedIndustryDetails,
      eventProps: {
        is_first_org: !accessibleOrganizations.length,
      },
    })
  }, [])

  const handleConsentChange = (): void => {
    setConsent(!consent)
    if (!consent) {
      clearErrors(ClaimFormFields.InternalUsageConsent)
    }
  }

  const saveClaim = async (claim: Omit<Claim, 'id'>): Promise<void> => {
    await mutateAsync(
      { url: endpoints.claims, body: claim },
      {
        onError: (error: RequestError) => {
          Sentry.captureException(error, {
            tags: { event: 'Error creating claim' },
            user: { id: String(account?.user?.id) },
            extra: { errorDetails: error.message, payload: claim },
          })
          showErrorNotification({ requestError: error })
        },
        onSuccess: async (claimCreationResult: ClaimCreationResult) => {
          trackEvent({
            name: AmplitudeTrackingEvents.Onboarding.AddOrganization.SubmittedForm,
            eventProps: {
              has_referral: Boolean(claim.details.referralCode),
              is_first_org: !accessibleOrganizations.length,
            },
          })
          if (claim.details.referralCode) {
            await unsetReferral()
          }
          if (claimCreationResult === ClaimCreationResult.RequiresReview) {
            await queryClient.invalidateQueries(FetchKey.Claim)
            onNextStep()
          } else {
            await queryClient.invalidateQueries(FetchKey.AccessibleOrganizations)
            showSnackbar({
              message: formatMessage({ id: 'notifications.organizationAutomaticallyCreated' }),
              severity: 'success',
            })
            closeDialog()
          }
        },
      },
    )
  }

  const onSubmit = (values: ClaimFormData) => {
    if (!consent) {
      setError(ClaimFormFields.InternalUsageConsent, {
        type: 'manual',
        message: formatMessage({ id: 'form.validation.selectionRequired' }),
      })
      return
    }

    const cleanFormValues = { ...values, ...values.industry, industry: undefined }
    delete cleanFormValues.industry

    const payload: Omit<Claim, 'id'> = {
      details: { ...cleanFormValues },
      objectType: 'organization',
    }

    if (referralCode) {
      const cleanedReferral = referralCode?.split(' ')[0]
      payload.details.referralCode = cleanedReferral
    }
    saveClaim(payload)
  }

  const { items } = useFetchCollectionWithPost<NaceCode>({
    key: FetchKey.NaceCodes,
    endpoint: endpoints.naceCodes,
    payload: {
      filter: [
        {
          name: 'level',
          filters: [
            {
              operator: Operators.In,
              value: [1, 2],
            },
          ],
        },
      ],
      sort: { target: 'description', order: SortOrder.ASCENDING },
      include: [],
    },
  })

  const naceCodeLevel1 = items.filter(naceCode => naceCode.level === 1)
  const naceCodeLevel2 = items.filter(naceCode => naceCode.level === 2)

  const naceCodeOptions = naceCodeLevel2
    .map(naceCode => {
      const parentNaceCode = naceCodeLevel1.find(parentNaceCode => parentNaceCode.id === naceCode.parentId)

      return {
        label: naceCode.description,
        value: {
          industryId: naceCode.parentId,
          industryName: parentNaceCode?.description ?? '',
          subIndustryId: naceCode.id,
        },
      }
    })
    .sort((a, b) => -b.value.industryName.localeCompare(a.value.industryName))

  return (
    <OnboardingForm
      preHeader={formatMessage({ id: 'claim.createClaim.orgInfoStep.preHeader' })}
      header={formatMessage({ id: 'claim.createClaim.orgInfoStep.header' })}
    >
      <Select<NaceCodeLevelOption>
        hoveringLabel
        name={ClaimFormFields.Industry}
        control={control}
        rules={{ required: formatMessage({ id: 'form.validation.selectionRequired' }) }}
        groupBy={option => option.value.industryName ?? ''}
        findSelectedValue={(value, option) =>
          value?.industryId === option.value?.industryId && value?.subIndustryId === option.value?.subIndustryId
        }
        options={naceCodeOptions}
        fieldLabel={formatMessage({ id: 'claim.createClaim.orgInfoStep.industry' })}
      />
      {errors?.industry?.message && <ErrorDisplay message={errors.industry.message} />}
      <FormControl>
        <FormLabel sx={{ mb: 2, mt: 2 }}>{formatMessage({ id: 'claim.companySize.label' })}</FormLabel>
        <Controller
          name={ClaimFormFields.CompanySizeRange}
          control={control}
          rules={{ required: formatMessage({ id: 'form.validation.selectionRequired' }) }}
          render={({ value, onChange }): JSX.Element => (
            <Stack direction="row" flexWrap="wrap">
              {Object.values(CompanyRanges).map(range => (
                <SelectButton
                  key={range}
                  startIcon={value === range && <Check />}
                  isSelected={value === range}
                  onClick={() => onChange(range)}
                >
                  {formatMessage({ id: `claim.companySize.${range}` })}
                </SelectButton>
              ))}
            </Stack>
          )}
        />
        {errors?.companySizeRange?.message && <ErrorDisplay message={errors.companySizeRange.message} />}
      </FormControl>
      <FormControl>
        <FormLabel sx={{ mb: 2, mt: 1 }}>{formatMessage({ id: 'claim.companyRevenue.label' })}</FormLabel>
        <Controller
          name={ClaimFormFields.RevenueRange}
          control={control}
          render={({ value, onChange }): JSX.Element => (
            <Stack direction="row" flexWrap="wrap">
              {Object.values(CompanyRanges).map(range => (
                <SelectButton
                  key={range}
                  startIcon={value === range && <Check />}
                  isSelected={value === range}
                  onClick={() => onChange(value === range ? undefined : range)}
                >
                  {formatMessage({ id: `claim.companyRevenue.${range}` })}
                </SelectButton>
              ))}
            </Stack>
          )}
        />
      </FormControl>
      <FormControl>
        <FormControlLabel
          sx={{ pt: 1, display: 'flex', alignItems: 'flex-start' }}
          control={
            <Checkbox
              sx={{ mt: 0.5 }}
              checked={consent}
              onChange={handleConsentChange}
              name={ClaimFormFields.InternalUsageConsent}
            />
          }
          labelPlacement="end"
          label={
            <Typography p={1} variant="body2">
              {formatMessage({ id: 'claim.claimConsent' })}
            </Typography>
          }
        />
        {errors?.internalUsageConsent?.message && <ErrorDisplay message={errors?.internalUsageConsent?.message} />}
      </FormControl>
      <Stack pt={2} direction="row" justifyContent="space-between">
        <Button startIcon={<ArrowBack />} onClick={onPreviousStep}>
          {formatMessage({ id: 'general.back' })}
        </Button>
        <LoadingButton type="submit" variant="contained" onClick={handleSubmit(onSubmit)} loading={isLoading}>
          {formatMessage({ id: 'general.submit' })}
        </LoadingButton>
      </Stack>
    </OnboardingForm>
  )
}

export default OrgInfoStep
